import React, { PropsWithChildren } from "react";
import Tab, { TabProps } from "./Tab";
import { css, useTheme } from "@emotion/react";
import Typography from "../typography/Typography";

export interface TabsProps extends PropsWithChildren {
  stretch?: boolean;
  style?: "underline" | "underlineFilled";
}

const Tabs: React.FC<TabsProps> = (props) => {
  const theme = useTheme();
  const style = props.style || "underlineFilled";
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);

  const toRender: React.ReactElement<TabProps>[] = [];
  React.Children.forEach(props.children, (child) => {
    if (!React.isValidElement(child)) {
      return null;
    }
    if (child.type !== Tab) {
      return null;
    }

    toRender.push(child as React.ReactElement<TabProps>);
  });

  const tabListCss = css({
    display: "flex",
    listStyle: "none",
    padding: 0,
    margin: 0,
    gap: 8,
    borderBottom: `1px solid ${theme.colours.grey["200"]}`,
    overflow: "scroll",
  });
  const activeTabCss = css({
    borderBottom: `2px solid ${theme.colours.primary["700"]}`,
    backgroundColor:
      style === "underlineFilled" ? theme.colours.primary["50"] : undefined,
  });
  const tabCss = css({
    display: "flex",
    justifyContent: "center",
    flex: props.stretch ? 1 : undefined,
    cursor: "pointer",
    padding: "12px",
    borderBottom: `2px solid transparent`,
    "&:hover": {
      ...activeTabCss,
    },
    transition: "all 0.2s ease-in-out",
  });

  return (
    <div>
      <ul css={tabListCss}>
        {toRender.map((r, i) => (
          <li
            key={r.props.label}
            onClick={() => setActiveTabIndex(i)}
            css={[tabCss, activeTabIndex === i ? activeTabCss : {}]}
          >
            <Typography
              text={r.props.label}
              color={
                activeTabIndex === i
                  ? theme.colours.primary["700"]
                  : theme.colours.grey["500"]
              }
              size="sm"
              weight="semibold"
              extraCss={{
                whiteSpace: "nowrap",
              }}
            />
          </li>
        ))}
      </ul>
      {toRender[activeTabIndex]}
    </div>
  );
};

export default Tabs;
